import type { Action } from '@0xtorch/core'
import { and, eq, or, sql } from 'drizzle-orm'
import { actionSourceXrplSchema } from '../../querySchema'
import {
  accountTable,
  actionAccountRelationTable,
  actionSourceTable,
  actionTable,
  lockedPeriodActionTable,
  portfolioTable,
  xrplTransactionIndexTable,
} from '../../schema'
import type { ActionSourceXrpl, DatabaseWithTransaction } from '../../types'
import { applyActionEditRulesToInsertedActionsOnDatabase } from './applyActionEditRulesToInsertedActions'
import { deleteAssistantProposalsForSourceIds } from './deleteAssistantProposalsForActionKeys'
import {
  applyRulesToLockedInsertRowsInTransaction,
  createAssetInsertQueries,
  createLockedActionInsertQueries,
  createOpenActionInsertQueries,
  filterActionAccountRelations,
  getClosedAccountingPeriodBoundary,
  prepareFiscalLockInsertRows,
} from './fiscalLock'

/**
 * 成功した XRPL Source のみを Action と解析済み索引に原子的に保存する。
 * 解析済み Source は再生成せず、完全再同期では索引の reset により再解析を許可する。
 * @param params DB、取引 Source、既存形式に変換・価格付け済みの Action
 * @returns 保存または既存編集を保護する skip の完了を表す Promise
 */
export const saveXrplActions = async (params: {
  database: DatabaseWithTransaction
  source: ActionSourceXrpl
  actions: readonly Action[]
}): Promise<void> => {
  const { database, transaction } = params.database
  const source = actionSourceXrplSchema.parse(params.source)
  const indexes = await database
    .select()
    .from(xrplTransactionIndexTable)
    .where(
      and(
        eq(xrplTransactionIndexTable.sourceId, source.id),
        eq(xrplTransactionIndexTable.analyzed, false),
      ),
    )
  if (indexes.length === 0) return
  const timestamp = indexes[0].timestamp.getTime()
  if (indexes.some((index) => index.timestamp.getTime() !== timestamp)) {
    throw new Error('Xrpl source timestamps do not match')
  }
  const orders = new Set<number>()
  for (const action of params.actions) {
    if (
      action.source !== source.id ||
      action.timestamp !== timestamp ||
      !Number.isSafeInteger(action.order) ||
      action.order < 0 ||
      orders.has(action.order)
    ) {
      throw new Error('Xrpl action does not match its indexed source')
    }
    orders.add(action.order)
  }
  const accounts = await database
    .select({ id: xrplTransactionIndexTable.accountId })
    .from(xrplTransactionIndexTable)
    .where(eq(xrplTransactionIndexTable.sourceId, source.id))
  const [manuallyLocked] = await database
    .select({ id: actionTable.id })
    .from(actionTable)
    .where(
      and(
        eq(actionTable.sourceId, source.id),
        or(
          eq(actionTable.locked, true),
          eq(actionTable.evidence, 'manual-check'),
          eq(actionTable.evidence, 'user-rule'),
        ),
      ),
    )
    .limit(1)
  const boundary = await getClosedAccountingPeriodBoundary({
    database: params.database,
  })
  const isClosed = boundary !== undefined && timestamp <= boundary.endTimestamp
  const actions = manuallyLocked === undefined ? params.actions : []
  const relations = actions.flatMap((action) =>
    accounts.map((account) => ({
      actionId: `${action.source}_${action.order}`,
      accountId: account.id,
    })),
  )
  const prepared = await prepareFiscalLockInsertRows({
    database: params.database,
    actions,
    createLockedActionAccountRelations: ({ lockedActions }) =>
      filterActionAccountRelations({
        actionAccountRelations: relations,
        actionIds: lockedActions.map(
          (action) => `${action.source}_${action.order}`,
        ),
      }),
  })
  await transaction([
    (tx) => [
      (async () => {
        if (manuallyLocked === undefined) {
          await tx
            .insert(actionSourceTable)
            .values(source)
            .onConflictDoUpdate({ target: actionSourceTable.id, set: source })
          if (!isClosed) {
            await deleteAssistantProposalsForSourceIds({
              database: tx,
              sourceIds: [source.id],
            })
            await tx
              .delete(actionTable)
              .where(eq(actionTable.sourceId, source.id))
            await tx.update(portfolioTable).set({ isTxGenerated: false })
          } else {
            await tx
              .delete(lockedPeriodActionTable)
              .where(eq(lockedPeriodActionTable.sourceId, source.id))
          }
        }
      })(),
    ],
    (tx) =>
      createAssetInsertQueries({
        tx,
        openInsertRows: prepared.openInsertRows,
        lockedInsertRows: prepared.lockedInsertRows,
      }),
    (tx) => [
      (async () => {
        if (manuallyLocked === undefined) {
          const lockedInsertRows =
            await applyRulesToLockedInsertRowsInTransaction({
              database: tx,
              lockedInsertRows: prepared.lockedInsertRows,
            })
          await Promise.all([
            ...createOpenActionInsertQueries({
              tx,
              openInsertRows: prepared.openInsertRows,
              actionAccountRelationRows: filterActionAccountRelations({
                actionAccountRelations: relations,
                actionIds: prepared.openActionIds,
              }),
            }),
            ...createLockedActionInsertQueries({ tx, lockedInsertRows }),
          ])
          if (prepared.openActionIds.length > 0) {
            await applyActionEditRulesToInsertedActionsOnDatabase({
              database: tx,
              actionIdList: prepared.openActionIds,
            })
          }
        }
        if (manuallyLocked !== undefined) {
          const preserved = await tx
            .select({ id: actionTable.id })
            .from(actionTable)
            .where(eq(actionTable.sourceId, source.id))
          for (const action of preserved) {
            for (const account of accounts) {
              await tx
                .insert(actionAccountRelationTable)
                .values({ actionId: action.id, accountId: account.id })
                .onConflictDoNothing()
            }
          }
        }
        await tx
          .update(xrplTransactionIndexTable)
          .set({ analyzed: true })
          .where(eq(xrplTransactionIndexTable.sourceId, source.id))
        for (const account of accounts) {
          await tx
            .update(accountTable)
            .set({
              syncing: sql`(coalesce(${accountTable.xrplIndexComplete}, 0) = 0 OR EXISTS (SELECT 1 FROM ${xrplTransactionIndexTable} WHERE ${xrplTransactionIndexTable.accountId} = ${account.id} AND ${xrplTransactionIndexTable.analyzed} = 0))`,
            })
            .where(eq(accountTable.id, account.id))
        }
      })(),
    ],
  ])
}
